import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Modal, Icon, Tooltip } from 'antd'

/**
 * PhotoLightbox
 *
 * One zoomable viewer for every photo in the cockpit, so a thumbnail in the
 * table, a pane in the comparison and a candidate card in the PV picker all
 * behave the same way. Reconciliation decisions rest on telling two similar
 * objects apart — a serial plate, a scuff, a cable — which a 96px thumbnail
 * cannot settle.
 *
 * Controls: scroll or +/- to zoom, drag (or arrow keys) to pan once zoomed,
 * double-click to toggle, 0 to reset, [ and ] to move between images, Esc to
 * close. Zoom and pan reset whenever the image changes, so a new photo is never
 * shown already scrolled off-centre.
 *
 * Props:
 *  - images  : [{ url, caption?, sub? }] — the set to page through
 *  - index   : which one is showing
 *  - onIndex : (i) => void
 *  - visible / onClose
 */

const MIN = 1
const MAX = 6
const STEP = 0.35

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v))

const PhotoLightbox = ({ visible, images = [], index = 0, onIndex, onClose }) => {
  const [scale, setScale] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const drag = useRef(null)
  const stage = useRef(null)

  const list = (images || []).filter(im => im && im.url)
  const i = clamp(index, 0, Math.max(list.length - 1, 0))
  const img = list[i] || null

  const reset = useCallback(() => { setScale(1); setPan({ x: 0, y: 0 }) }, [])

  // A different photo always starts fitted and centred.
  useEffect(() => { reset() }, [i, visible, reset])

  const zoomTo = useCallback((next) => {
    setScale(prev => {
      const s = clamp(next, MIN, MAX)
      if (s === MIN) setPan({ x: 0, y: 0 })   // fitting again re-centres
      return s
    })
  }, [])

  const step = (i2) => { if (onIndex && list.length > 1) onIndex((i + i2 + list.length) % list.length) }

  // Wheel zooms rather than scrolling the page behind the modal.
  useEffect(() => {
    const el = stage.current
    if (!el || !visible) return
    const onWheel = (e) => {
      e.preventDefault()
      setScale(prev => {
        const s = clamp(prev + (e.deltaY < 0 ? STEP : -STEP), MIN, MAX)
        if (s === MIN) setPan({ x: 0, y: 0 })
        return s
      })
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [visible])

  useEffect(() => {
    if (!visible) return
    const onKey = (e) => {
      // Handled here rather than left to antd, whose Esc-to-close only fires when
      // focus is inside the modal — and this viewer opens over another modal, so
      // the key must close the TOP one only.
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); return }
      if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomTo(scale + STEP) }
      else if (e.key === '-' || e.key === '_') { e.preventDefault(); zoomTo(scale - STEP) }
      else if (e.key === '0') { e.preventDefault(); reset() }
      else if (e.key === '[') { e.preventDefault(); step(-1) }
      else if (e.key === ']') { e.preventDefault(); step(1) }
      else if (scale > 1 && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault()
        const d = 40
        setPan(p => ({
          x: p.x + (e.key === 'ArrowLeft' ? d : e.key === 'ArrowRight' ? -d : 0),
          y: p.y + (e.key === 'ArrowUp' ? d : e.key === 'ArrowDown' ? -d : 0)
        }))
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [visible, scale, i, list.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const onMouseDown = (e) => {
    if (scale <= 1) return
    drag.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y }
    e.preventDefault()
  }
  const onMouseMove = (e) => {
    if (!drag.current) return
    setPan({ x: drag.current.px + (e.clientX - drag.current.x), y: drag.current.py + (e.clientY - drag.current.y) })
  }
  const endDrag = () => { drag.current = null }

  if (!visible || !img) return null

  return (
    <Modal
      visible={visible} onCancel={onClose} footer={null} closable={false}
      width="92%" centered destroyOnClose bodyStyle={{ padding: 0 }}
      className="rcx-lightbox" maskClosable
    >
      <div className="lb">
        <div className="lb-head">
          <div className="lb-cap">
            <b>{img.caption || 'Photo'}</b>
            {img.sub ? <span className="lb-sub">{img.sub}</span> : null}
            {list.length > 1 && <span className="lb-count mono">{i + 1} / {list.length}</span>}
          </div>
          <div className="lb-tools">
            <Tooltip title="Zoom out  ( − )">
              <button className="lb-btn" onClick={() => zoomTo(scale - STEP)} disabled={scale <= MIN}>
                <Icon type="zoom-out" />
              </button>
            </Tooltip>
            <span className="lb-pct mono">{Math.round(scale * 100)}%</span>
            <Tooltip title="Zoom in  ( + )">
              <button className="lb-btn" onClick={() => zoomTo(scale + STEP)} disabled={scale >= MAX}>
                <Icon type="zoom-in" />
              </button>
            </Tooltip>
            <Tooltip title="Fit  ( 0 )">
              <button className="lb-btn" onClick={reset} disabled={scale === 1 && !pan.x && !pan.y}>
                <Icon type="fullscreen-exit" />
              </button>
            </Tooltip>
            <span className="lb-gap" />
            <Tooltip title="Close  ( Esc )">
              <button className="lb-btn" onClick={onClose}><Icon type="close" /></button>
            </Tooltip>
          </div>
        </div>

        <div className="lb-stage" ref={stage}
          onMouseDown={onMouseDown} onMouseMove={onMouseMove}
          onMouseUp={endDrag} onMouseLeave={endDrag}
          onDoubleClick={() => zoomTo(scale > 1 ? 1 : 2.5)}
          style={{ cursor: scale > 1 ? (drag.current ? 'grabbing' : 'grab') : 'zoom-in' }}>
          {list.length > 1 && (
            <button className="lb-nav prev" onClick={(e) => { e.stopPropagation(); step(-1) }}>
              <Icon type="left" />
            </button>
          )}
          <img src={img.url} alt={img.caption || ''} draggable={false}
            style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})` }} />
          {list.length > 1 && (
            <button className="lb-nav next" onClick={(e) => { e.stopPropagation(); step(1) }}>
              <Icon type="right" />
            </button>
          )}
        </div>

        <div className="lb-foot">
          scroll or <b>+</b>/<b>−</b> to zoom · drag to pan · double-click to toggle
          {list.length > 1 && <> · <b>[</b> <b>]</b> to move</>} · <b>0</b> to fit · <b>Esc</b> to close
        </div>
      </div>
    </Modal>
  )
}

export default PhotoLightbox
